import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { useCMS } from '../context/CMSContext';
import { testimonials as defaultTestimonials } from '../data/mockData';

const TestimonialsSection = () => {
  const { get } = useCMS();
  const list = get('testimonials') || defaultTestimonials;
  const [i, setI] = useState(0);
  const next = () => setI((v) => (v + 1) % list.length);
  const prev = () => setI((v) => (v - 1 + list.length) % list.length);
  const active = list[i];
  if (!active) return null;

  return (
    <section className="relative py-24 lg:py-32 bg-[#050914] border-t border-white/5 overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <p className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-white/50 font-bold mb-4">
              <span className="w-8 h-px bg-white/30" />
              Testimonials
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tighter text-white leading-[1.05] max-w-2xl">
              What clients say — placeholders pending real reviews.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={prev} className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white transition-colors" aria-label="Previous" data-testid="testimonial-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={next} className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white hover:border-white transition-colors" aria-label="Next" data-testid="testimonial-next-btn">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={i}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5 }}
            className="relative rounded-2xl border border-white/10 bg-[#0B1221] p-10 lg:p-14">
            <Quote className="absolute top-8 right-8 w-10 h-10 text-white/8" />
            <p className="font-display text-2xl md:text-3xl lg:text-4xl text-white leading-[1.25] font-medium tracking-tight max-w-4xl">
              &ldquo;{active.quote || active.text}&rdquo;
            </p>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-white/15 bg-white/5 flex items-center justify-center">
                <span className="font-display text-lg text-white/70">{(active.company || active.name || '?')[0]}</span>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{active.name || active.company}</p>
                <p className="text-white/45 text-xs mt-0.5">{active.company && active.name ? active.company : ''}{active.industry ? ` · ${active.industry}` : ''}</p>
              </div>
              {active.placeholder && (
                <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-white/30 font-bold border border-white/10 rounded-full px-3 py-1">Placeholder</span>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-6 flex gap-2 justify-center">
          {list.map((t, idx) => (
            <button key={t.id || idx} onClick={() => setI(idx)}
              className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? 'w-8 bg-[#FF6B35]' : 'w-1.5 bg-white/20 hover:bg-white/40'}`}
              aria-label={`Go to testimonial ${idx + 1}`}
              data-testid={`testimonial-dot-${idx}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
